import { displayText, roleLabel } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
// AddCaseForm.jsx — RACE CONDITION + PERF FIXED

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import api, { getCsrfToken } from '../../../../services/api';
import ThemeContext from '../../../../ThemeContext';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { parsePhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ClientConfig from '../../../../../config/Clientconfig';
import { URL_SEGMENTS } from '../../../../../config/Routesconfig';

const onlyLettersAndSpaces = /^[A-Za-z\s]+$/;

const schema = yup.object().shape({
  case_name: yup
    .string()
    .matches(onlyLettersAndSpaces, () => displayText('Case Name must contain only letters and spaces'))
    .min(3, () => displayText('Case Name must be at least 3 characters'))
    .max(50, () => displayText('Case Name cannot exceed 50 characters'))
    .required(() => displayText('Case Name is required')),
  assign_client: yup.string().required(() => displayText('Case assign manager is required')),
  case_matter_number: yup
    .string()
    .required(() => displayText('Case or Matter Number is required'))
    .matches(/^[0-9]{1,10}$/, () => displayText('Case or Matter Number must be 1-10 digits only')),
  first_name: yup
    .string()
    .matches(onlyLettersAndSpaces, { message: 'First Name must contain only letters and spaces', excludeEmptyString: true })
    .max(50, 'First Name cannot exceed 50 characters')
    .nullable(),
  last_name: yup
    .string()
    .matches(onlyLettersAndSpaces, { message: 'Last Name must contain only letters and spaces', excludeEmptyString: true })
    .max(50, 'Last Name cannot exceed 50 characters')
    .nullable(),
  phone: yup
    .string()
    .required('Phone number is required')
    .test('is-valid-phone', 'Invalid phone number', function (value) {
      try {
        const phoneToValidate = value.startsWith('+') ? value : `+${value}`;
        const phoneNumber = parsePhoneNumber(phoneToValidate);
        return phoneNumber && phoneNumber.isValid();
      } catch {
        return false;
      }
    }),
  email: yup.string().email('Invalid email format').required('Email is required'),
  country: yup.string().nullable().notRequired(),
  address: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup.string().nullable(),
  zip: yup.string().nullable(),
});

const TYPE_COLORS = {
  client_admin:      { bg: '#E1F5EE', color: '#0F6E56', gradient: 'linear-gradient(135deg,#28a745,#1e7e34)' },
  enterprise_client: { bg: '#FAEEDA', color: '#854F0B', gradient: 'linear-gradient(135deg,#ffc107,#e0a800)' },
  manual:            { bg: '#F1EFE8', color: '#5F5E5A', gradient: 'linear-gradient(135deg,#6c757d,#545b62)' },
  client_user:       { bg: '#E6F1FB', color: '#185FA5', gradient: 'linear-gradient(135deg,#17a2b8,#138496)' },
};

const getTypeColors = (type) => TYPE_COLORS[type] || TYPE_COLORS.client_user;

const getBadgeLabel = (type) => {
  if (type === 'client_admin')      return roleLabel(type, 'Admin');
  if (type === 'enterprise_client') return 'Enterprise';
  if (type === 'manual')            return 'Manual';
  if (type === 'client_user')       return roleLabel(type, 'User');
  return 'User';
};

// ─
// ManagerDropdown (unchanged from your original)
// ─
const ManagerDropdown = ({ label, clients, selectedClients, onSelect, onRemove, error, onBlurValidate }) => {
  useTerminology();
  const [searchTerm,   setSearchTerm]   = useState('');
  const [open,         setOpen]         = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = clients.filter((c) => {
    if (selectedClients.some((s) => s.uniqueKey === c.uniqueKey)) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.email && c.email.toLowerCase().includes(q))
    );
  });

  const exactMatch      = clients.some((c) => c.name.toLowerCase() === searchTerm.trim().toLowerCase());
  const showAddManually = searchTerm.trim().length > 0 && filtered.length === 0 && !exactMatch;

  const handleSelect = (client) => {
    onSelect(client);
    setSearchTerm('');
    setOpen(false);
  };

  const handleAddManually = () => {
    const name = searchTerm.trim();
    if (!name) return;
    const ts = Date.now();
    onSelect({
      id:        null,
      name,
      email:     '',
      type:      'manual',
      uniqueKey: `manual_${ts}`,
    });
    setSearchTerm('');
    setOpen(false);
  };

  const borderColor = error ? '#dc3545' : inputFocused ? '#378ADD' : '#ced4da';
  const focusShadow = error
    ? '0 0 0 3px rgba(220,53,69,0.15)'
    : '0 0 0 3px rgba(55,138,221,0.15)';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <label style={{ fontSize: '10px', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
        {label}*
      </label>

      <div
        style={{
          display:      'flex',
          alignItems:   'center',
          height:       '35px',
          border:       `1px solid ${borderColor}`,
          borderRadius: '6px',
          background:   '#fff',
          transition:   'border-color 0.15s, box-shadow 0.15s',
          boxShadow:    inputFocused ? focusShadow : 'none',
          overflow:     'hidden',
        }}
      >
        <input
          type="text"
          value={searchTerm}
          placeholder="Search and select a manager…"
          autoComplete="off"
          onChange={(e) => { setSearchTerm(e.target.value); setOpen(true); }}
          onFocus={() => { setInputFocused(true); setOpen(true); }}
          onBlur={() => {
            setInputFocused(false);
            setTimeout(() => { setOpen(false); onBlurValidate(); }, 180);
          }}
          style={{
            flex:       1,
            border:     'none',
            outline:    'none',
            padding:    '0 10px',
            fontSize:   '11px',
            background: 'transparent',
            color:      '#212529',
            fontFamily: 'inherit',
            height:     '100%',
          }}
        />
        <span style={{ padding: '0 10px', display: 'flex', alignItems: 'center', color: '#9ca3af', flexShrink: 0 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      </div>

      {showAddManually && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleAddManually}
          style={{
            display:      'flex',
            alignItems:   'center',
            gap:          '6px',
            width:        '100%',
            padding:      '7px 10px',
            marginTop:    '5px',
            background:   '#E6F1FB',
            border:       '1px dashed #85B7EB',
            borderRadius: '6px',
            color:        '#185FA5',
            fontSize:     '11px',
            cursor:       'pointer',
            fontWeight:   500,
            fontFamily:   'inherit',
            transition:   'background 0.15s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#B5D4F4')}
          onMouseOut={(e)  => (e.currentTarget.style.background = '#E6F1FB')}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="#185FA5">
            <path d="M12.146 1.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-4 1.5a.5.5 0 0 1-.65-.65l1.5-4a.5.5 0 0 1 .11-.168l10-10z" />
          </svg>
          Add&nbsp;<strong>"{searchTerm}"</strong>&nbsp;manually
        </button>
      )}

      {open && (
        <div
          style={{
            position:     'absolute',
            top:          'calc(100% + 4px)',
            left:         0,
            right:        0,
            zIndex:       1050,
            background:   '#fff',
            border:       '1px solid #dee2e6',
            borderRadius: '6px',
            boxShadow:    '0 8px 24px rgba(0,0,0,0.10)',
            maxHeight:    '210px',
            overflowY:    'auto',
          }}
        >
          {filtered.length > 0 ? (
            filtered.map((client) => {
              const colors = getTypeColors(client.type);
              return (
                <div
                  key={client.uniqueKey}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(client)}
                  style={{
                    display:        'flex',
                    justifyContent: 'space-between',
                    alignItems:     'center',
                    padding:        '9px 12px',
                    cursor:         'pointer',
                    borderBottom:   '0.5px solid #f0f0f0',
                    background:     'transparent',
                    transition:     'background 0.12s',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                  onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '12px', color: '#212529', fontWeight: 500 }}>
                        {client.name}
                      </span>
                      <span
                        style={{
                          fontSize:     '10px',
                          padding:      '1px 7px',
                          borderRadius: '20px',
                          background:   colors.bg,
                          color:        colors.color,
                          fontWeight:   500,
                          whiteSpace:   'nowrap',
                        }}
                      >
                        {getBadgeLabel(client.type)}
                      </span>
                    </div>
                    {client.email && (
                      <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                        {client.email}
                      </span>
                    )}
                  </div>
                  <span style={{ color: '#9ca3af', fontSize: '20px', lineHeight: 1, flexShrink: 0, marginLeft: '8px' }}>
                    +
                  </span>
                </div>
              );
            })
          ) : (
            <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#9ca3af' }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ marginRight: '5px', verticalAlign: 'middle' }}>
                <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.4" />
                <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              {searchTerm ? `No managers found for "${searchTerm}"` : 'No managers available'}
            </div>
          )}
        </div>
      )}

      {selectedClients.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <span style={{ fontSize: '10px', color: '#9ca3af', display: 'block', marginBottom: '5px' }}>
            Selected Manager
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {selectedClients.map((c) => {
              const colors = getTypeColors(c.type);
              return (
                <span
                  key={c.uniqueKey}
                  style={{
                    display:      'inline-flex',
                    alignItems:   'center',
                    gap:          '5px',
                    padding:      '4px 10px',
                    borderRadius: '20px',
                    background:   colors.bg,
                    color:        colors.color,
                    fontSize:     '11px',
                    fontWeight:   500,
                  }}
                >
                  {c.name}
                  <small style={{ fontSize: '10px', opacity: 0.75 }}>({getBadgeLabel(c.type)})</small>
                  <button
                    type="button"
                    onClick={() => onRemove(c)}
                    title={`Remove ${c.name}`}
                    style={{
                      border:     'none',
                      background: 'transparent',
                      cursor:     'pointer',
                      color:      colors.color,
                      fontSize:   '16px',
                      lineHeight: 1,
                      padding:    0,
                      marginLeft: '2px',
                      opacity:    0.6,
                      fontFamily: 'inherit',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseOut={(e)  => (e.currentTarget.style.opacity = '0.6')}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: '9px', color: '#dc3545', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </div>
  );
};

// ─
// AddCaseForm
// ─
const AddCaseForm = ({ isEdit = false }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);

  const [clients,          setClients]          = useState([]);
  const [selectedClients,  setSelectedClients]  = useState([]);
  const [countries,        setCountries]        = useState([]);
  const [statesList,       setStatesList]       = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [clientError,      setClientError]      = useState('');
  const [phoneInputKey]                         = useState(0);
  const [phoneError,       setPhoneError]       = useState('');

  const [emailDupWarning,  setEmailDupWarning]  = useState(false);
  const [phoneDupWarning,  setPhoneDupWarning]  = useState(false);
  const [emailConfirmed,   setEmailConfirmed]   = useState(false);
  const [phoneConfirmed,   setPhoneConfirmed]   = useState(false);

  // ── Ref holds the mapped clients list so async callbacks always see latest ──
  const clientsRef = useRef([]);

  const navigate = useNavigate();
  const { username, id } = useParams();

  const dashBase = `/${username}/dashboard`;
  const routes = {
    casesList: `${dashBase}/${URL_SEGMENTS.cases}`,
    editCase:  (cid) => `${dashBase}/${URL_SEGMENTS.editCase}/${cid}`,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      case_name: '', assign_client: '', case_matter_number: '',
      first_name: '', last_name: '', phone: '', email: '',
      country: '', address: '', city: '', state: '', zip: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const country = watch('country');
  const phone   = watch('phone');

  // ── FIX: fetchClientsAndUsers returns the mapped array so callers can use it directly ──
  const fetchClientsAndUsers = useCallback(async () => {
    try {
      const res = await api.get('case-managers');
      const raw = res.data.data || [];
      if (!raw.length) {
        toast.warn(displayText('No case managers available.'));
        return [];
      }
      const mapped = raw.map((m) => ({
        ...m,
        // ── uniqueKey always built from client_admin_id for client_admin ──
        uniqueKey:  m.type === 'client_admin'
          ? `client_admin-${m.client_admin_id}`
          : `${m.type}-${m.id}`,
        searchText: `${m.name} ${m.email || ''}`.toLowerCase(),
      }));
      // Update both state AND ref atomically
      setClients(mapped);
      clientsRef.current = mapped;
      return mapped;
    } catch {
      toast.error(displayText('Failed to load case managers.'));
      return [];
    }
  }, []);

  // ── FIX: prefillCase accepts the managers list directly — no ref needed ──
  const prefillCase = useCallback(async (managersArg) => {
    if (!id) return;
    try {
      const response = await api.get(`cases/${id}`);
      const caseData = response.data.data;

      setValue('case_name',          caseData.case_name          || '');
      setValue('case_matter_number', caseData.case_matter_number || '');
      setValue('first_name',         caseData.first_name         || '');
      setValue('last_name',          caseData.last_name          || '');
      setValue('phone',              caseData.phone              || '');
      setValue('email',              caseData.email              || '');
      setValue('country',            caseData.country            || 'United States');
      setValue('address',            caseData.address            || '');
      setValue('city',               caseData.city               || '');
      setValue('state',              caseData.state              || '');
      setValue('zip',                caseData.zip                || '');

      if (caseData.caseManager || caseData.client) {
        const manager     = caseData.caseManager || caseData.client;
        const managerType = caseData.case_manager_type || 'client_admin';

        let managerUniqueKey;
        if (managerType === 'client_admin') {
          // Try to match by client_admin_id, then user id, then user_id
          const match = managersArg.find(
            (c) => c.type === 'client_admin' && (
              String(c.client_admin_id) === String(caseData.case_manager_id) ||
              String(c.id)              === String(caseData.case_manager_id) ||
              String(c.user_id)         === String(caseData.case_manager_id)
            )
          );
          managerUniqueKey = match
            ? match.uniqueKey
            : `client_admin-${caseData.case_manager_id}`;
        } else if (managerType === 'client_user') {
          managerUniqueKey = `client_user-${caseData.case_manager_client_user_id || manager.id}`;
        } else {
          managerUniqueKey = `${managerType}-${manager.id}`;
        }

        const managerObj = {
          id:              manager.id,
          name:            manager.name || caseData.assign_case_manager || '',
          type:            managerType,
          client_user_id:  caseData.case_manager_client_user_id,
          client_admin_id: caseData.case_manager_id,
          email:           manager.email || '',
          uniqueKey:       managerUniqueKey,
        };
        setSelectedClients([managerObj]);
        setValue('assign_client', managerObj.name);
      }
    } catch (err) {
      console.error('Fetch case error:', err);
      toast.error(displayText('Failed to load case data'));
    }
  }, [id, setValue]);

  useEffect(() => {
    let cancelled = false;
  
    // Countries loads independently in background — does NOT block managers or matter number
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
  
    fetch('https://countriesnow.space/api/v0.1/countries', { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        clearTimeout(timeout);
        if (!cancelled && !data.error) {
          setCountries(data.data.map(c => c.country));
        }
      })
      .catch(() => {
        if (!cancelled) setCountries(['United States', 'Canada', 'Other']);
      });
  
    // Managers + matter number load immediately in parallel — nothing blocks them
    const init = async () => {
      try {
        const [managers] = await Promise.all([
          fetchClientsAndUsers(),
  
          // !isEdit
          //   ? api.get('cases/next-matter-number')
          //       .then(response => {
          //         if (!cancelled && response.data && response.data.success !== false) {
          //           const nextNum = response.data.next_number || response.data.nextNumber || '1';
          //           setValue('case_matter_number', nextNum);
          //           clearErrors('case_matter_number');
          //         }
          //       })
          //       .catch(() => {
          //         if (!cancelled) {
          //           setValue('case_matter_number', '1');
          //           clearErrors('case_matter_number');
          //           toast.warning('Could not auto-generate case number. Using default value.', { autoClose: 4000 });
          //         }
          //       })
             Promise.resolve(),
        ]);
  
        if (!cancelled && isEdit && managers && managers.length > 0) {
          await prefillCase(managers);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error('Failed to load form data.');
        }
      }
    };
  
    init();
  
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  // ── Fetch states when country changes 

  useEffect(() => {
    if (isEdit || selectedClients.length === 0) return;
  
    const managerId   = selectedClients[0]?.client_admin_id || selectedClients[0]?.id;
    const managerType = selectedClients[0]?.type;
    if (!managerId) return;
  
    api.get('cases/next-matter-number', {
      params: {
        case_manager_id:   managerId,
        case_manager_type: managerType,
      },
    })
      .then(res => {
        if (res.data?.success !== false) {
          setValue('case_matter_number', res.data.next_number || '1');
          clearErrors('case_matter_number');
        }
      })
      .catch(() => {
        setValue('case_matter_number', '1');
        clearErrors('case_matter_number');
        toast.warning(displayText('Could not auto-generate case number. Using default value.'), { autoClose: 4000 });
      });
  }, [selectedClients, isEdit]);

  useEffect(() => {
    if (!country) { setStatesList([]); setValue('state', ''); return; }
    (async () => {
      try {
        const res  = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ country }),
        });
        const data = await res.json();
        setStatesList((data.data?.states || []).map((s) => s.name));
      } catch { setStatesList([]); }
    })();
  }, [country, setValue]);

  // ── Manager dropdown callbacks ──────
  const handleSelect = (client) => {
    if (selectedClients.some((s) => s.uniqueKey === client.uniqueKey)) return;
    const next = [...selectedClients, client];
    setSelectedClients(next);
    setValue('assign_client', next.map((c) => c.name).join(', '));
    clearErrors('assign_client');
    setClientError('');
  };

  const handleRemove = (client) => {
    const next = selectedClients.filter((c) => c.uniqueKey !== client.uniqueKey);
    setSelectedClients(next);
    if (next.length === 0) {
      setValue('assign_client', '');
      setError('assign_client', { message: displayText('Case assign manager is required') });
      setClientError('Case assign manager is required');
    } else {
      setValue('assign_client', next.map((c) => c.name).join(', '));
      clearErrors('assign_client');
      setClientError('');
    }
  };

  const handleManagerBlur = () => {
    if (selectedClients.length === 0) {
      setError('assign_client', { message: displayText('Case assign manager is required') });
      setClientError('Case assign manager is required');
    } else {
      clearErrors('assign_client');
      setClientError('');
    }
  };

  // ── Email blur — real-time duplicate check ────────────────────────────────
  const handleEmailBlur = async () => {
    const emailVal = watch('email');
    if (!emailVal || errors.email) { setEmailDupWarning(false); return; }
    try {
      const res = await api.post('cases/check-duplicate', {
        email: emailVal, exclude_id: isEdit ? id : null,
      });
      if (res.data.errors?.email) {
        setEmailDupWarning(true); setEmailConfirmed(false);
      } else {
        setEmailDupWarning(false);
      }
    } catch { setEmailDupWarning(false); }
  };

  // ── Phone duplicate check ───────────
  const handlePhoneBlurDupCheck = async (phoneVal) => {
    if (!phoneVal) { setPhoneDupWarning(false); return; }
    try {
      const res = await api.post('cases/check-duplicate', {
        phone: phoneVal, exclude_id: isEdit ? id : null,
      });
      if (res.data.errors?.phone) {
        setPhoneDupWarning(true); setPhoneConfirmed(false);
      } else {
        setPhoneDupWarning(false);
      }
    } catch { setPhoneDupWarning(false); }
  };

  const handlePhoneChange = (value) => {
    setValue('phone', value.startsWith('+') ? value : `+${value}`);
    if (phoneError)      setPhoneError('');
    if (phoneDupWarning) { setPhoneDupWarning(false); setPhoneConfirmed(false); }
    if (errors.phone)    trigger('phone');
  };

  const handlePhoneBlur = () => {
    const val = watch('phone');
    if (!val) { setPhoneError('Phone number is required'); return; }
    try {
      const parsed = parsePhoneNumber(val.startsWith('+') ? val : `+${val}`);
      if (!parsed?.isValid()) { setPhoneError('Invalid phone number'); return; }
      setPhoneError('');
      trigger('phone');
      handlePhoneBlurDupCheck(val);
    } catch { setPhoneError('Invalid phone number'); }
  };

  // ── Submit ──────────────────────────
  const onSubmit = async (data) => {
    if (selectedClients.length === 0) {
      setClientError('Case assign manager is required');
      setError('assign_client', { message: displayText('Case assign manager is required') });
      await trigger();
      return;
    }

    setClientError('');
    clearErrors('assign_client');

    if (data.phone && phoneError) {
      toast.error('Please fix the phone number error before submitting');
      return;
    }

    if (emailDupWarning && !emailConfirmed) {
      toast.warning('Please confirm if you want to proceed with the existing email.');
      return;
    }
    if (phoneDupWarning && !phoneConfirmed) {
      toast.warning('Please confirm if you want to proceed with the existing phone number.');
      return;
    }

    setLoading(true);

    try {
      await getCsrfToken();

      const selectedManager = selectedClients[0];

      let caseManagerId;
      if (selectedManager.type === 'client_admin') {
        caseManagerId = selectedManager.client_admin_id || selectedManager.id;
      } else if (selectedManager.type === 'client_user') {
        caseManagerId = selectedManager.client_user_id || selectedManager.id;
      } else {
        caseManagerId = selectedManager.user_id || selectedManager.id;
      }

      const submitData = {
        ...data,
        country: data.country || 'United States',
        address: data.address || '',
        city:    data.city    || '',
        state:   data.state   || '',
        zip:     data.zip     || '',
        allow_duplicate_email: emailConfirmed ? 1 : 0,
        allow_duplicate_phone: phoneConfirmed ? 1 : 0,
        case_manager_id:       caseManagerId,
        case_manager_type:     selectedManager.type,
        case_manager_client_user_id: selectedManager.type === 'client_user'
          ? (selectedManager.client_user_id || selectedManager.id)
          : null,
        assign_case_manager: selectedManager.name,
      };

      if (isEdit) {
        await api.put(`cases/${id}`, submitData);
        toast.success(displayText('Case updated successfully'));
      } else {
        await api.post('cases', submitData);
        toast.success(displayText('Case created successfully'));
      }

      navigate(routes.casesList);

    } catch (error) {
      console.error('API Error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Something went wrong';
      toast.error(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Shared styles ───────────────────
  const inputStyle = (hasError) => ({
    fontSize:        '11px',
    padding:         '0 10px',
    height:          '35px',
    backgroundColor: theme.colors.bgPrimary,
    color:           theme.colors.textPrimary,
    border:          `1px solid ${hasError ? '#dc3545' : theme.colors.borderColor}`,
    borderRadius:    '6px',
    width:           '100%',
    outline:         'none',
    fontFamily:      'inherit',
    boxSizing:       'border-box',
    display:         'block',
    transition:      'border-color 0.15s, box-shadow 0.15s',
  });

  const labelStyle = {
    fontSize:     '10px',
    color:        theme.colors.textSecondary || '#6c757d',
    display:      'block',
    marginBottom: '5px',
  };

  const errStyle = { fontSize: '9px', color: '#dc3545', marginTop: '4px' };

  const warningBoxStyle = {
    marginTop:    '6px',
    padding:      '8px 10px',
    borderRadius: '6px',
    background:   '#FFF8E1',
    border:       '1px solid #FFD54F',
    fontSize:     '10px',
    color:        '#7B5800',
  };

  const confirmBtnStyle = (color) => ({
    fontSize:     '10px',
    padding:      '3px 10px',
    borderRadius: '4px',
    background:   color,
    color:        '#fff',
    border:       'none',
    cursor:       'pointer',
    fontFamily:   'inherit',
  });

  return (
    <>
      <nav aria-label="breadcrumb" style={{ fontSize: '10px', margin: '5px 5px 5px 25px' }}>
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to={routes.casesList} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>
              {ClientConfig.cases.managementLabel}
            </Link>
          </li>
          <li className="breadcrumb-item active" style={{ color: theme.colors.textPrimary }}>
          {isEdit 
    ? `Edit ${ClientConfig.cases.label}` 
    : `Add ${ClientConfig.cases.label}`
  }
          </li>
        </ol>
      </nav>

      <div
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color:           theme.colors.textPrimary,
          fontSize:        'clamp(10px,2.5vw,11px)',
          borderRadius:    '12px',
          padding:         '1.5rem',
          boxShadow:       '0 4px 12px rgba(0,0,0,0.08)',
          border:          `1px solid ${theme.colors.borderColor || '#ddd'}`,
        }}
      >
        <style>{`
          .afc-input:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 3px ${theme.colors.primary}25 !important;
            outline: none;
          }
          .afc-input:hover { border-color: ${theme.colors.primary} !important; }
          .react-tel-input .form-control {
            width:100%; height:35px; padding:6px 10px 6px 45px;
            font-size:11px; font-family:inherit;
            color:${theme.colors.textPrimary};
            background-color:${theme.colors.bgPrimary};
            border:1px solid ${theme.colors.borderColor};
            border-radius:6px;
          }
          .react-tel-input .form-control:focus {
            border-color:${theme.colors.primary};
            box-shadow:0 0 0 3px ${theme.colors.primary}25;
          }
          .react-tel-input .flag-dropdown {
            border:1px solid ${theme.colors.borderColor};
            background-color:${theme.colors.bgPrimary};
            border-right:none; border-radius:6px 0 0 6px; height:35px;
          }
          .react-tel-input .selected-flag:hover { background-color:${theme.colors.bgLight}; }
          .react-tel-input .country-list {
            background-color:${theme.colors.bgPrimary};
            border:1px solid ${theme.colors.borderColor};
            box-shadow:0 6px 16px rgba(0,0,0,0.1);
            max-height:200px; overflow-y:auto; font-size:11px;
          }
          .react-tel-input .country-list .country:hover { background-color:${theme.colors.bgLight}; }
          .react-tel-input .country-list .country.highlight {
            background-color:${theme.colors.primary}; color:#fff;
          }
        `}</style>

        <ToastContainer
          position="top-right" autoClose={3000} hideProgressBar={false}
          newestOnTop={false} closeOnClick rtl={false}
          pauseOnFocusLoss draggable pauseOnHover theme="light"
        />

        {loading && (
          <div className="position-fixed top-50 start-50 translate-middle" style={{ zIndex: 1000 }}>
            <div className="spinner-border text-primary" style={{ width: '30px', height: '30px' }} role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}

        <h4 style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.textPrimary, marginBottom: '1rem' }}>
        {isEdit 
  ? `Edit ${ClientConfig.cases.label}` 
  : `Add ${ClientConfig.cases.label}`
}
        </h4>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div
            style={{
              backgroundColor: theme.colors.bgPrimary,
              border:          `1px solid ${theme.colors.borderColor}`,
              borderRadius:    '8px',
              padding:         '1rem',
              marginBottom:    '1rem',
            }}
          >
            {/* ════ ROW 1 ════ */}
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label style={labelStyle}>{ClientConfig.cases.label} Name*</label>
                <input type="text" className="afc-input" {...register('case_name')} style={inputStyle(errors.case_name)} />
                {errors.case_name && <div style={errStyle}>{errors.case_name.message}</div>}
              </div>

              <div className="col-md-4">
                <ManagerDropdown
                  label={`Assign ${ClientConfig.cases.label} Manager`}
                  clients={clients}
                  selectedClients={selectedClients}
                  onSelect={handleSelect}
                  onRemove={handleRemove}
                  error={clientError || errors.assign_client?.message}
                  onBlurValidate={handleManagerBlur}
                />
              </div>

              <div className="col-md-4">
                <label style={labelStyle}>{ClientConfig.cases.label} or Matter Number*</label>
                <input
                  type="text"
                  className="afc-input"
                  {...register('case_matter_number')}
                  disabled={!isEdit}
                  readOnly={!isEdit}
                  style={{
                    ...inputStyle(errors.case_matter_number),
                    backgroundColor: isEdit ? theme.colors.bgPrimary : '#f8f9fa',
                    cursor:          isEdit ? 'text' : 'not-allowed',
                  }}
                />
                {errors.case_matter_number && <div style={errStyle}>{errors.case_matter_number.message}</div>}
              </div>
            </div>

            {/* ════ Billing header ════ */}
            <p style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.primary, marginBottom: '2px', marginTop: '8px' }}>
              Billing Information
            </p>
            <p style={{ fontSize: '10px', color: theme.colors.textMuted, fontStyle: 'italic', marginBottom: '12px' }}>
              (This is the contact person in your Firm or organization that receives the communication for the lead)
            </p>

            {/* ════ ROW 2 ════ */}
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label style={labelStyle}>First Name</label>
                <input type="text" className="afc-input" {...register('first_name')} style={inputStyle(errors.first_name)} />
                {errors.first_name && <div style={errStyle}>{errors.first_name.message}</div>}
              </div>
              <div className="col-md-3">
                <label style={labelStyle}>Last Name</label>
                <input type="text" className="afc-input" {...register('last_name')} style={inputStyle(errors.last_name)} />
                {errors.last_name && <div style={errStyle}>{errors.last_name.message}</div>}
              </div>
            </div>

            {/* ════ ROW 3 ════ */}
            <div className="row g-3 mb-3">
              {/* Phone */}
              <div className="col-md-3">
                <label style={labelStyle}>Primary Contact (Mobile)*</label>
                <PhoneInput
                  key={phoneInputKey}
                  country={'us'}
                  value={phone || ''}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  inputStyle={{
                    width: '100%', height: '35px', fontSize: '11px', fontFamily: 'inherit',
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${errors.phone || phoneError ? '#dc3545' : theme.colors.borderColor}`,
                    borderRadius: '6px', color: theme.colors.textPrimary, padding: '6px 10px 6px 45px',
                  }}
                  containerStyle={{ width: '100%' }}
                  inputProps={{ id: 'phone', name: 'phone' }}
                  buttonStyle={{
                    border: `1px solid ${theme.colors.borderColor}`,
                    backgroundColor: theme.colors.bgPrimary,
                    borderRight: 'none', borderRadius: '6px 0 0 6px', height: '35px',
                  }}
                />
                {(errors.phone || phoneError) && (
                  <div style={errStyle}>{errors.phone?.message || phoneError}</div>
                )}
                {!errors.phone && !phoneError && phoneDupWarning && !phoneConfirmed && (
                  <div style={warningBoxStyle}>
                    <div style={{ marginBottom: '6px' }}>{displayText(" This phone number is already used in one of your cases. Do you want to proceed? ")}</div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button type="button" style={confirmBtnStyle('#4CAF50')}
                        onClick={() => { setPhoneConfirmed(true); setPhoneDupWarning(false); }}>
                        Yes, proceed
                      </button>
                      <button type="button" style={confirmBtnStyle('#f44336')}
                        onClick={() => { setPhoneDupWarning(false); setPhoneConfirmed(false); }}>
                        No, change number
                      </button>
                    </div>
                  </div>
                )}
                {!errors.phone && !phoneError && phoneConfirmed && (
                  <div style={{ fontSize: '10px', color: '#4CAF50', marginTop: '4px' }}>
                    ✓ Proceeding with existing phone number
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="col-md-3">
                <label style={labelStyle}>Email*</label>
                <input
                  type="email"
                  className="afc-input"
                  {...register('email', { onBlur: handleEmailBlur })}
                  style={inputStyle(errors.email)}
                />
                {errors.email && <div style={errStyle}>{errors.email.message}</div>}
                {!errors.email && emailDupWarning && !emailConfirmed && (
                  <div style={warningBoxStyle}>
                    <div style={{ marginBottom: '6px' }}>{displayText(" This email is already used in one of your cases. Do you want to proceed? ")}</div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button type="button" style={confirmBtnStyle('#4CAF50')}
                        onClick={() => { setEmailConfirmed(true); setEmailDupWarning(false); }}>
                        Yes, proceed
                      </button>
                      <button type="button" style={confirmBtnStyle('#f44336')}
                        onClick={() => { setEmailDupWarning(false); setEmailConfirmed(false); setValue('email', ''); }}>
                        No, change email
                      </button>
                    </div>
                  </div>
                )}
                {!errors.email && emailConfirmed && (
                  <div style={{ fontSize: '10px', color: '#4CAF50', marginTop: '4px' }}>
                    ✓ Proceeding with existing email
                  </div>
                )}
              </div>
            </div>

            {/* ════ ROW 4 ════ */}
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label style={labelStyle}>Country</label>
                <select className="afc-input" {...register('country')} style={{ ...inputStyle(errors.country), appearance: 'auto' }}>
                  <option value="">Select Country</option>
                  {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.country && <div style={errStyle}>{errors.country.message}</div>}
              </div>
              <div className="col-md-3">
                <label style={labelStyle}>Address</label>
                <input type="text" className="afc-input" {...register('address')} style={inputStyle(errors.address)} />
                {errors.address && <div style={errStyle}>{errors.address.message}</div>}
              </div>
            </div>

            {/* ════ ROW 5 ════ */}
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label style={labelStyle}>City</label>
                <input type="text" className="afc-input" {...register('city')} style={inputStyle(errors.city)} />
                {errors.city && <div style={errStyle}>{errors.city.message}</div>}
              </div>
              <div className="col-md-3">
                <label style={labelStyle}>State</label>
                <select className="afc-input" {...register('state')} disabled={!country}
                  style={{ ...inputStyle(errors.state), appearance: 'auto', opacity: !country ? 0.6 : 1 }}>
                  <option value="">Select State</option>
                  {statesList.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.state && <div style={errStyle}>{errors.state.message}</div>}
              </div>
              <div className="col-md-3">
                <label style={labelStyle}>ZIP Code</label>
                <input type="text" className="afc-input" {...register('zip')} style={inputStyle(errors.zip)} />
                {errors.zip && <div style={errStyle}>{errors.zip.message}</div>}
              </div>
            </div>
          </div>

          {/* ════ Actions ════ */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Link
              to={routes.casesList}
              style={{
                fontSize: '11px', padding: '8px 18px', borderRadius: '6px',
                border: `1px solid ${theme.colors.borderColor}`,
                color: theme.colors.textSecondary || '#6c757d',
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
              }}
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              style={{
                fontSize: '11px', padding: '8px 18px', borderRadius: '6px', border: 'none',
                backgroundColor: theme.colors.primary, color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                opacity: loading ? 0.75 : 1, fontFamily: 'inherit', transition: 'opacity 0.15s',
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm"
                    style={{ width: '12px', height: '12px' }} role="status">
                    <span className="visually-hidden">Loading…</span>
                  </span>
                  Saving…
                </>
              ) : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddCaseForm;